// The #4314 conflict resolver: draining a device’s conflict backlog.
//
// Extracted from DatabaseWrapper so it can be unit-tested without pulling in
// pouchdb-browser, which references `self` and fails to load under node. It was
// split out of database-wrapper-passthrough.ts, which had become the shared home
// for every testable sync helper regardless of subject (deferred finding
// df-08d4a072f17b).

import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { docTypeFromId } from './sync-telemetry-shaping';
import {
  decideConflictMergePolicy,
  ConflictMergeDecision,
  resolveMergedTransactionStamp,
  decideConflictRunStep,
  ConflictRunStep,
  ConflictRunConditions,
  CONFLICT_RESOLVE_YIELD_MS,
} from './sync-conflict-resolve-decisions';
import {
  ConflictArchiveDeps,
  archiveLosingRevisionsAsync,
  sweepConflictArchiveAsync,
} from './database-wrapper-conflict-archive';
import { DateTime } from 'luxon';

// ---------------------------------------------------------------------------
// #4314 Phase C — the resolver.
//
// Drains a device's conflict backlog: for each conflicted document, merge the
// losing branches into the winner where the gates allow, archive every losing
// body, then close each losing branch. The deletion tombstones replicate, so the
// branch closes on the server CouchDB too — but only because the push reports
// every branch tip rather than just the winning revision. That is not the
// default our sync would otherwise use, and it did not hold until #4433 fixed
// it; see `DatabaseWrapper.USER_DB_PUSH_CHANGES_STYLE`.
//
// Dependency-injected in the same shape as the recovery helpers in
// database-wrapper-save-recovery.ts, so the whole thing is testable under node
// without a PouchDB instance.
//
// See design-docs/sync-integrity.md ("Phase C — On-Device Conflict Auto-Resolve").
// ---------------------------------------------------------------------------

/** The database calls the resolver needs, injected so it is node-testable. */
export interface ConflictResolveDeps {
  /** Re-read the winner fresh, asking PouchDB to surface its conflict leaves. */
  getWithConflictsAsync: (docId: string) => Promise<any>;
  /** Read one specific revision's body. */
  getRevisionAsync: (docId: string, rev: string) => Promise<any>;
  /** Write the merged winner. */
  putDocAsync: (doc: any) => Promise<{ rev: string }>;
  /**
   * Close ONE losing revision. Deliberately one revision per call — see the
   * comment on the delete loop below for why the resolver keeps that shape.
   */
  deleteRevisionAsync: (docId: string, rev: string, transactionStamp: string | undefined) => Promise<string | undefined>;
  /**
   * The LIVE in-memory copy the identity map holds for this document, or
   * undefined when nothing holds it. "Held" usually means a long-lived cache
   * still references it, not that the user has it on screen (#5842). Injected
   * because the resolver has no other way to know, and skipping this check
   * silently breaks the promise that the cleanup never disturbs an object the
   * app is using.
   */
  heldCopy: (docId: string) => HeldCopy | undefined;
  /** Run one document's whole resolve inside the write mutex. */
  withWriteLock: <T>(work: () => Promise<T>) => Promise<T>;
  /** The archive's own injected database calls. */
  archive: ConflictArchiveDeps;
  /** Re-read the live abort conditions — checked between EVERY document. */
  readRunConditions: () => Pick<ConflictRunConditions,
    'appVisible' | 'online' | 'signedIn' | 'impersonating' | 'shuttingDown' | 'databaseRecreating'>;
  /** Called after a merged winner lands, so the caller can refresh the identity
   *  map and record the revision as locally authored. */
  onWinnerWritten: (docId: string, newRev: string, mergedBody: any) => void;
  /** Called after a losing revision is closed, so the caller can record the
   *  tombstone revision as locally authored. */
  onRevisionClosed: (docId: string, rev: string) => void;
  /** Epoch milliseconds. Injected so tests control time. */
  nowMs: () => number;
  /** Pause between documents so user saves can take the write mutex. */
  yieldAsync: (ms: number) => Promise<void>;
  /** This device's id, for the merged winner's write stamp. */
  deviceId: string;
  logError: (message: string) => void;
  /** Surface a per-document failure. Nothing is ever swallowed. */
  reportFailure: (docId: string, stage: string, err: unknown) => void;
  /**
   * Report a losing revision the version list still names but whose stored
   * content is gone (#6415), with what was done about it. Kept apart from
   * `reportFailure` on purpose: it is lasting damage, not a resolver failure,
   * and sharing the failure canary would hide a real failure behind it.
   */
  reportMissingBody: (docId: string, rev: string, outcome: MissingBodyOutcome) => void;
  /**
   * Whether this document never leaves the device (`DEVICE_LOCAL_DOC_IDS`).
   * Only such a document may have a body-missing revision closed: on a synced
   * one the tombstone would also close the server's copy, which may still hold
   * the content (#6415).
   */
  neverSyncs: (docId: string) => boolean;
}

/** What the resolver did with a losing revision whose content is gone (#6415). */
export enum MissingBodyOutcome {
  /** Closed: the document never syncs, so no other copy can exist. */
  Closed = 'Closed',
  /** Left as it is: the document syncs, and the server may still hold the content. */
  LeftInPlace = 'LeftInPlace',
}

/**
 * What the resolver needs to know about a document the app holds in memory.
 * "Held" and "which revision" are separate facts on purpose: a held object can
 * carry no `_rev` at all (a singleton registered before its document existed,
 * or after a read failed), and folding that into "not held" would let a merge
 * write onto it.
 */
export interface HeldCopy {
  /** The held object's own `_rev` — what its next save writes against. */
  rev: string | undefined;
}

/** Everything one resolver run did, shaped for the `sync_conflicts_resolved` event. */
export interface ConflictResolveResult {
  resolvedDocCount: number;
  /** Documents where a merge was ATTEMPTED — counted before the write. */
  mergedDocCount: number;
  closedWithoutMergeCount: number;
  /** Documents where that write actually LANDED, so the visible content really
   *  did change. Gates widening the ramp: a no-op closure and a content change
   *  carry completely different risk. It trails `mergedDocCount` by exactly the
   *  number of merges whose write failed, which is why they are two counters
   *  and not one. */
  winnerChangedCount: number;
  closedG1Count: number;
  closedG2Count: number;
  closedG3Count: number;
  /** The subset of G2 closures where a write stamp was missing or unparseable. */
  missingStampCount: number;
  /** Documents G2 would have closed, where a losing copy of a log entry held more of the user's
   *  data than the shown one and was written as the winner instead (#6834). Also counted in
   *  `mergedDocCount` and, once the write lands, `winnerChangedCount`. */
  adoptedHigherRankCount: number;
  deletedRevCount: number;
  archivedCount: number;
  failedDocCount: number;
  unreadableRevCount: number;
  /** Losing revisions found with their stored content gone for good (#6415).
   *  Counted when found, whether they were then closed or left in place. */
  missingBodyRevCount: number;
  deferredOpenDocCount: number;
  archiveBackstopEvictedCount: number;
  archiveEvictedWithinRetentionCount: number;
  /** Archive operations that failed this run — see the sweep result's own note. */
  archiveFailureCount: number;
  docTypes: string[];
  /** True when the run stopped on a limit rather than finishing the list. */
  capped: boolean;
  /** ACTUAL WORK time, excluding the deliberate pauses. */
  durationMs: number;
  /** Wall-clock time the run spanned, pauses included. Reported alongside the
   *  work time so "1.5 s of work" can be told apart from "1.5 s of work spread
   *  over four minutes", which is what the user would actually notice. */
  wallMs: number;
  documentsRemaining: number;
  /** True when the run resolved or closed at least one document. Drives the
   *  resume marker: progress means continue soon, no progress means back off a
   *  day, so an infinite retry loop is not reachable. */
  madeProgress: boolean;
}

function emptyConflictResolveResult(): ConflictResolveResult {
  return {
    resolvedDocCount: 0, mergedDocCount: 0, closedWithoutMergeCount: 0, winnerChangedCount: 0,
    closedG1Count: 0, closedG2Count: 0, closedG3Count: 0, missingStampCount: 0, adoptedHigherRankCount: 0,
    deletedRevCount: 0, archivedCount: 0, failedDocCount: 0, unreadableRevCount: 0, missingBodyRevCount: 0,
    deferredOpenDocCount: 0, archiveBackstopEvictedCount: 0, archiveEvictedWithinRetentionCount: 0,
    archiveFailureCount: 0,
    docTypes: [], capped: false, durationMs: 0, wallMs: 0, documentsRemaining: 0, madeProgress: false,
  };
}

/** What resolving ONE document concluded. Separated out so the per-document work
 *  is readable on its own and the loop above it stays about pacing. */
enum ResolveDocOutcome {
  Resolved = 'Resolved',
  /** The app holds it and resolving it now would disturb the held copy — try
   *  again on a later run. */
  Deferred = 'Deferred',
  /** Sync already resolved it, or a losing revision could not be read. */
  Skipped = 'Skipped',
  Failed = 'Failed',
}

/** What the second read concluded about a losing revision that answered "not found". */
enum NotFoundRevisionReading {
  /** Still a losing leaf: the version list names it, but its content is gone (#6415). */
  BodyMissing = 'BodyMissing',
  /** No longer a losing leaf: sync closed or extended it between the two reads. */
  NoLongerLosing = 'NoLongerLosing',
  /** The second read itself failed, so nothing can be concluded. */
  ReReadFailed = 'ReReadFailed',
}

/** The second read's answer, carrying its error when it failed so the caller can report it. */
type NotFoundRevisionAnswer =
  | { reading: NotFoundRevisionReading.BodyMissing | NotFoundRevisionReading.NoLongerLosing }
  | { reading: NotFoundRevisionReading.ReReadFailed, error: unknown };

/**
 * Read the document again after a losing revision answered "not found", to tell
 * lasting damage from a race with sync (#6415).
 *
 * Sync bypasses the write mutex the resolver holds, so between the conflict
 * listing and the body read it can legitimately extend a losing branch, after
 * which auto-compaction deletes that revision's body. Then the revision is no
 * longer a losing leaf. If the fresh read still lists it, its content is gone
 * for good. A later race (sync extending it after this read) still fails safe:
 * the tombstone write is then refused as a conflict and nothing is deleted.
 */
async function readNotFoundRevisionAgainAsync(
  deps: ConflictResolveDeps,
  docId: string,
  rev: string,
): Promise<NotFoundRevisionAnswer> {
  let fresh: any;
  try {
    fresh = await deps.getWithConflictsAsync(docId);
  } catch (err) {
    // the whole document went away in between: sync changed it, nothing is wrong
    if (isPouchNotFound(err)) return { reading: NotFoundRevisionReading.NoLongerLosing };
    // any other failure travels to the caller, which reports it
    return { reading: NotFoundRevisionReading.ReReadFailed, error: err };
  }
  const stillLosing = (fresh?._conflicts ?? []).includes(rev);
  return { reading: stillLosing ? NotFoundRevisionReading.BodyMissing : NotFoundRevisionReading.NoLongerLosing };
}

/** What reading a document's losing revisions produced (#4314 step 2, #6415). */
interface LosingRevisionsRead {
  losingBodies: { rev: string, body: any }[];
  /** Body-missing revisions this run may close: on a document that never syncs only. */
  bodyMissingRevsToClose: string[];
  /** True when the whole document must be left for a later run. */
  leaveForLaterRun: boolean;
}

/**
 * Read every losing body of one document (#4314 step 2).
 *
 * A revision that cannot be read is NEVER deleted on a guess: a failed read is
 * normally a temporary problem (an IndexedDB hiccup, a SQLite lock), not absent
 * data, and deleting on a transient glitch would destroy real user data
 * permanently. It is reported, and the whole document is tried again later.
 *
 * The one exception is a "not found" answer for a revision that is STILL a losing
 * leaf when the document is read again: the version list names it but its stored
 * content is gone (#6415: the pre-v1.31.5 #4417 write race left such leaves on
 * some iPhones). It is closed only when the document NEVER syncs. On a document
 * that syncs, the tombstone would replicate, and the server may still hold that
 * revision open with its content, so closing it there could destroy the last
 * copy. A synced document is reported and left exactly as it is.
 */
async function readLosingRevisionsAsync(
  deps: ConflictResolveDeps,
  docId: string,
  losingRevs: string[],
  result: ConflictResolveResult,
): Promise<LosingRevisionsRead> {
  const read: LosingRevisionsRead = { losingBodies: [], bodyMissingRevsToClose: [], leaveForLaterRun: false };
  for (const rev of losingRevs) {
    let readError: unknown;
    try {
      read.losingBodies.push({ rev, body: await deps.getRevisionAsync(docId, rev) });
      continue;
    } catch (err) {
      // kept and handled below, so the second read stays out of this catch
      readError = err;
    }

    if (!isPouchNotFound(readError)) {
      result.unreadableRevCount++;
      deps.reportFailure(docId, 'read-losing-revision', readError);
      read.leaveForLaterRun = true;
      continue;
    }

    const answer = await readNotFoundRevisionAgainAsync(deps, docId, rev);
    switch (answer.reading) {
      case NotFoundRevisionReading.BodyMissing:
        result.missingBodyRevCount++;
        if (deps.neverSyncs(docId)) {
          // reported once it is actually closed, in the close loop
          read.bodyMissingRevsToClose.push(rev);
        } else {
          deps.reportMissingBody(docId, rev, MissingBodyOutcome.LeftInPlace);
          read.leaveForLaterRun = true;
        }
        break;
      case NotFoundRevisionReading.NoLongerLosing:
        // sync closed or extended this branch between the two reads, which also
        // removes its content legitimately. Nothing is wrong; the next run sees
        // the new state.
        read.leaveForLaterRun = true;
        break;
      case NotFoundRevisionReading.ReReadFailed:
        result.unreadableRevCount++;
        deps.reportFailure(docId, 'read-losing-revision', answer.error);
        read.leaveForLaterRun = true;
        break;
    }
  }
  return read;
}

/**
 * Resolve the conflicts on ONE document (#4314 R1). Called inside the write
 * mutex, one document per acquisition.
 *
 * The ordering here is the whole safety argument, so it is worth stating plainly:
 * nothing is deleted until the merged winner is written AND every losing body is
 * archived. A failure at either step aborts this document with no deletes at all,
 * leaving the conflict in place for a later run. Merge-and-preserve strictly
 * precedes destroy.
 */
async function resolveOneDocAsync(
  deps: ConflictResolveDeps,
  docId: string,
  result: ConflictResolveResult,
): Promise<ResolveDocOutcome> {
  // 1. re-read fresh: the enumerating sweep's snapshot may be stale, and sync
  // may have resolved this document in the meantime
  let winner: any;
  try {
    winner = await deps.getWithConflictsAsync(docId);
  } catch (err) {
    if (isPouchNotFound(err)) return ResolveDocOutcome.Skipped;
    deps.reportFailure(docId, 'read-winner', err);
    return ResolveDocOutcome.Failed;
  }
  const losingRevs: string[] = winner?._conflicts ?? [];
  if (losingRevs.length === 0) return ResolveDocOutcome.Skipped;

  // 2. read every losing body. readLosingRevisionsAsync says which failures leave
  // the document alone and which revision is closed without a body. If anything
  // is left for later, leave the WHOLE document rather than half-closing it.
  const { losingBodies, bodyMissingRevsToClose, leaveForLaterRun } =
    await readLosingRevisionsAsync(deps, docId, losingRevs, result);
  if (leaveForLaterRun) return ResolveDocOutcome.Skipped;

  // 3. the gates decide, and hand back the merged body so the merge is
  // computed exactly once. With no readable losing body there is nothing to
  // merge, so the document is closed without one — the gates are never handed
  // an empty list.
  const bodies = losingBodies.map(entry => entry.body);
  const policy = bodies.length > 0 ? decideConflictMergePolicy(docId, winner, bodies) : undefined;
  // adopting a losing copy as the winner (#6834) writes the winner just as a merge does, so it
  // takes the same path through every step below
  const merging = policy?.decision === ConflictMergeDecision.Merge
    || policy?.decision === ConflictMergeDecision.AdoptHigherRankedCopy;

  // 4. if the app holds a live copy of this document, defer it when resolving
  // now would disturb that copy. Checked here, after the decision and before
  // the archive, so a deferral still writes and deletes nothing.
  //
  // Two cases disturb it:
  // - a MERGE. Writing the merged winner refreshes the identity map, which
  //   force-writes onto the held object; its one guard is an in-flight save,
  //   which this resolver makes false BY CONSTRUCTION because it holds the write
  //   mutex. So a held Workout — the most common conflicted type, and
  //   deliberately not gated by G1 — would be rewritten under the app, arrays
  //   included.
  // - a held copy that is NOT on the winning revision. Closing the branch it
  //   sits on turns its next save into a conflict instead of an ordinary save.
  //
  // A close-only decision on a copy that sits on the winner disturbs nothing:
  // the winner's revision and body are untouched, so nothing refreshes the held
  // object. That is 356 of the first 358 production resolutions, which is why
  // deferring every held document kept the most-used documents conflicted for a
  // whole session (#5842).
  // a held copy with no revision is not on the winner either, so it defers too
  const held = deps.heldCopy(docId);
  const resolvingWouldDisturbHeldCopy = held !== undefined
    && (merging || held.rev !== winner._rev);
  if (resolvingWouldDisturbHeldCopy) {
    result.deferredOpenDocCount++;
    return ResolveDocOutcome.Deferred;
  }

  switch (policy?.decision) {
    case ConflictMergeDecision.CloseOnlyStructural: result.closedG1Count++; break;
    case ConflictMergeDecision.CloseOnlyTooOld: result.closedG2Count++; break;
    case ConflictMergeDecision.CloseOnlyMissingStamp:
      result.closedG2Count++;
      result.missingStampCount++;
      break;
    case ConflictMergeDecision.CloseOnlyNoChange: result.closedG3Count++; break;
    case ConflictMergeDecision.AdoptHigherRankedCopy: result.adoptedHigherRankCount++; break;
    default: break;
  }

  // 5. archive EVERY losing body, on every path — a close-only decision destroys
  // a branch just as surely as a merge does, so it archives too. A failed archive
  // aborts this document with no deletes, exactly like a failed put. A
  // body-missing revision has nothing to archive, so it has no entry.
  //
  // THE ORDER OF STEPS 5, 6 AND 7 IS THE SAFETY INVARIANT, not a stylistic
  // choice: preserve first, write second, destroy last. Do not reorder them.
  if (policy) {
    try {
      await archiveLosingRevisionsAsync(
        deps.archive, docId, losingBodies, policy.decision,
        DateTime.fromMillis(deps.nowMs()).toUTC().toISO() as string,
      );
      result.archivedCount += losingBodies.length;
    } catch (err) {
      deps.reportFailure(docId, 'archive', err);
      return ResolveDocOutcome.Failed;
    }
  }

  // the stamp both the merged winner and the tombstones are written with: the
  // MAXIMUM of the inputs, never "now". A janitorial "now" would make this
  // cleanup look like the newest write the document ever received, so
  // resurrected old content would beat the user's next genuine edit.
  const writeStamp = resolveMergedTransactionStamp(winner, bodies, deps.deviceId);

  // 6. write the merged winner, if the gates allowed a merge
  if (merging && policy) {
    const merged = policy.mergedBody;
    if (writeStamp) merged.db__trans = writeStamp;
    // the winner was read with {conflicts: true}, and the merge carries every
    // `_`-prefixed key across, so drop the conflict listing before writing it
    // back — it is read-time metadata, not part of the document
    delete merged._conflicts;
    // counted before the attempt, so a merge that was attempted but whose write
    // failed is distinguishable from one that actually landed
    result.mergedDocCount++;
    let newRev: string;
    try {
      newRev = (await deps.putDocAsync(merged)).rev;
    } catch (err) {
      // a 409 means the winner advanced under us — abort cleanly and retry the
      // whole document next run. Nothing has been deleted at this point.
      deps.reportFailure(docId, 'put-merged-winner', err);
      return ResolveDocOutcome.Failed;
    }
    // the write LANDED, so the visible content really did change
    result.winnerChangedCount++;
    merged._rev = newRev;
    // hand the post-put revision back so the caller can refresh the identity map
    // and record the revision as locally authored. Deliberately OUTSIDE the try
    // above: this walks the identity map and notifies watchers, which is app
    // code that can throw, and a failure there must not be reported as a put
    // failure on a put that succeeded.
    try {
      deps.onWinnerWritten(docId, newRev, merged);
    } catch (err) {
      deps.reportFailure(docId, 'refresh-identity-map', err);
    }
  } else {
    result.closedWithoutMergeCount++;
  }

  // 7. only now, with the winner written and every losing body preserved, close
  // the losing branches, and the body-missing ones of a never-synced document,
  // which had nothing to preserve.
  //
  // ONE WRITE PER REVISION, never a batch containing several revisions of the
  // same document.
  //
  // This rule was originally written because batching was believed to close only
  // the last revision on the custom iOS SQLite adapter. #4431 measured that on
  // the real adapter and it is NOT true — batched tombstones close every branch,
  // because the adapter's tree merge mutates the tree it is handed in place, so
  // each row in a batch already sees the previous row's work.
  //
  // The rule stays because it is the right shape for two other reasons: one
  // document's whole close stays inside a single write-mutex hold, and the run
  // can re-check its abort conditions and yield between documents. Keep it as a
  // deliberate choice, not as a workaround for something that was never broken.
  // The full measurement is in design-docs/sync-integrity.md.
  const revsToClose = [...losingBodies.map(entry => entry.rev), ...bodyMissingRevsToClose];
  let closedAll = true;
  for (const rev of revsToClose) {
    try {
      // the delete returns the TOMBSTONE's revision, which is what the local
      // changes feed reports — recording the revision we just deleted instead
      // would never match, so the tracker entry would do nothing
      const tombstoneRev = await deps.deleteRevisionAsync(docId, rev, writeStamp);
      result.deletedRevCount++;
      deps.onRevisionClosed(docId, tombstoneRev ?? rev);
      if (bodyMissingRevsToClose.includes(rev)) deps.reportMissingBody(docId, rev, MissingBodyOutcome.Closed);
    } catch (err) {
      // a partial failure is retried next run — never assumed done
      closedAll = false;
      deps.reportFailure(docId, 'close-losing-revision', err);
    }
  }
  if (!closedAll) return ResolveDocOutcome.Failed;

  result.resolvedDocCount++;
  return ResolveDocOutcome.Resolved;
}

/**
 * Drain a device's conflict backlog (#4314 R1/R6/R6b).
 *
 * Takes the candidate ids the enumerating sweep already found. The sweep's own
 * cost is charged to a SEPARATE allowance by the caller and deliberately not to
 * this run's work budget — charging it here would deadlock the drain on exactly
 * the heaviest accounts, which is the case this feature exists for.
 *
 * Never throws: a failure on one document is reported and the run moves on, and
 * the whole run is fire-and-forget from a background health tick.
 */
export async function resolveDocConflictsAsync(
  deps: ConflictResolveDeps,
  candidateIds: string[],
): Promise<ConflictResolveResult> {
  const result = emptyConflictResolveResult();
  const docTypes = new Set<string>();
  let workMs = 0;
  const runStartedMs = deps.nowMs();
  let index = 0;

  for (; index < candidateIds.length; index++) {
    const step = decideConflictRunStep({
      ...deps.readRunConditions(),
      workMs,
      wallMs: deps.nowMs() - runStartedMs,
      documentsAttempted: index,
    });
    if (step !== ConflictRunStep.Continue) {
      // an abort is not "capped" — the run was cut short by the app's state, not
      // by its own limits, and conflating them would misreport the drain
      result.capped = step === ConflictRunStep.StopWorkBudget || step === ConflictRunStep.StopDocumentCap;
      break;
    }

    // pause BETWEEN documents (never before the first) so user saves can take
    // the write mutex and the event loop can render. The pause is deliberately
    // outside the work measurement below.
    if (index > 0) await deps.yieldAsync(CONFLICT_RESOLVE_YIELD_MS);

    const docId = candidateIds[index];
    const docStartedMs = deps.nowMs();
    let outcome: ResolveDocOutcome;
    try {
      // one document per write-mutex acquisition, always released before the
      // next pause — never hold it across two documents
      outcome = await deps.withWriteLock(() => resolveOneDocAsync(deps, docId, result));
    } catch (err) {
      // the lock helper itself failed; surface it and keep going
      deps.reportFailure(docId, 'write-lock', err);
      outcome = ResolveDocOutcome.Failed;
    }
    workMs += deps.nowMs() - docStartedMs;

    if (outcome === ResolveDocOutcome.Failed) result.failedDocCount++;
    if (outcome === ResolveDocOutcome.Resolved) docTypes.add(docTypeFromId(docId));
  }

  result.documentsRemaining = Math.max(0, candidateIds.length - index);
  result.durationMs = workMs;
  result.wallMs = deps.nowMs() - runStartedMs;
  result.docTypes = [...docTypes];
  // progress counts a close-only document too: closing a duplicate branch is
  // real work even though the visible document did not change
  result.madeProgress = result.resolvedDocCount > 0;

  // sweep the archive once per run, after the real work. A sweep failure never
  // aborts a drain that already succeeded.
  try {
    const swept = await sweepConflictArchiveAsync(deps.archive, deps.nowMs());
    result.archiveBackstopEvictedCount = swept.archiveBackstopEvictedCount;
    result.archiveEvictedWithinRetentionCount = swept.archiveEvictedWithinRetentionCount;
    result.archiveFailureCount += swept.archiveFailureCount;
  } catch (err) {
    result.archiveFailureCount++;
    deps.logError(`conflict resolve: archive sweep failed — ${describeErrorForLog(err)}`);
  }

  return result;
}
